import { Checkbox, FormControlLabel, styled, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef } from 'react';
import Text from 'shell/components/Text';
import { v4 as uuid } from 'uuid';

const CustomCheckbox = styled(Checkbox)(({ theme, error }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2),
        '&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-4px',
        },
        '&:hover': {
            background: 'transparent',
            '&:not(.Mui-checked) rect': {
                ...(!error && {
                    stroke: theme.palette.ensightful.neutral[700],
                }),
            },
        },
        '&:active': {
            '&:not(.Mui-checked) rect': {
                ...(!error && {
                    stroke: theme.palette.ensightful.neutral[800],
                }),
            },
        },
        '& > input': {
            width: 40,
            height: 40,
        },
    };
});

const CustomFormControlLabel = styled(FormControlLabel)(({ theme }) => {
    return {
        gap: theme.spacing(1),
        margin: 0,
    };
});

const getIcon = (theme, checked, error, disabled) => {
    if (checked) {
        return disabled ? (
            <SvgIcon>
                <rect width={'24'} height={'24'} rx={'2'} fill={theme.palette.ensightful.neutral[300]} />
                <path
                    d={
                        'M19.6867 6.34175C19.786 6.44981 19.8648 6.57817 19.9186 6.7195C19.9723 6.86082 20 7.01233 20 7.16534C20 7.31835 19.9723 7.46985 19.9186 7.61118C19.8648 7.7525 19.786 7.88087 19.6867 7.98893L10.0886 18.4583C9.98958 18.5666 9.8719 18.6525 9.74233 18.7112C9.61277 18.7698 9.47387 18.8 9.3336 18.8C9.19332 18.8 9.05442 18.7698 8.92486 18.7112C8.7953 18.6525 8.67761 18.5666 8.57855 18.4583L4.31275 13.8052C4.1125 13.5868 4 13.2905 4 12.9816C4 12.6727 4.1125 12.3765 4.31275 12.158C4.513 11.9396 4.7846 11.8169 5.0678 11.8169C5.35099 11.8169 5.62259 11.9396 5.82284 12.158L9.3336 15.9898L18.1766 6.34175C18.2757 6.23342 18.3933 6.14747 18.5229 6.08883C18.6525 6.03019 18.7914 6 18.9316 6C19.0719 6 19.2108 6.03019 19.3404 6.08883C19.4699 6.14747 19.5876 6.23342 19.6867 6.34175Z'
                    }
                    fill={theme.palette.ensightful.neutral[500]}
                />
            </SvgIcon>
        ) : (
            <SvgIcon>
                <rect width={'24'} height={'24'} rx={'2'} fill={theme.palette.ensightful.primary[500]} />
                <path
                    d={
                        'M19.6867 6.34175C19.786 6.44981 19.8648 6.57817 19.9186 6.7195C19.9723 6.86082 20 7.01233 20 7.16534C20 7.31835 19.9723 7.46985 19.9186 7.61118C19.8648 7.7525 19.786 7.88087 19.6867 7.98893L10.0886 18.4583C9.98958 18.5666 9.8719 18.6525 9.74233 18.7112C9.61277 18.7698 9.47387 18.8 9.3336 18.8C9.19332 18.8 9.05442 18.7698 8.92486 18.7112C8.7953 18.6525 8.67761 18.5666 8.57855 18.4583L4.31275 13.8052C4.1125 13.5868 4 13.2905 4 12.9816C4 12.6727 4.1125 12.3765 4.31275 12.158C4.513 11.9396 4.7846 11.8169 5.0678 11.8169C5.35099 11.8169 5.62259 11.9396 5.82284 12.158L9.3336 15.9898L18.1766 6.34175C18.2757 6.23342 18.3933 6.14747 18.5229 6.08883C18.6525 6.03019 18.7914 6 18.9316 6C19.0719 6 19.2108 6.03019 19.3404 6.08883C19.4699 6.14747 19.5876 6.23342 19.6867 6.34175Z'
                    }
                    fill={theme.palette.ensightful.shades[0]}
                />
            </SvgIcon>
        );
    } else {
        if (disabled) {
            return (
                <SvgIcon>
                    <rect
                        x={'0.5'}
                        y={'0.5'}
                        width={'23'}
                        height={'23'}
                        rx={'1.5'}
                        fill={theme.palette.ensightful.shades[0]}
                    />
                    <rect
                        x={'0.5'}
                        y={'0.5'}
                        width={'23'}
                        height={'23'}
                        rx={'1.5'}
                        fill={theme.palette.ensightful.neutral[300]}
                        stroke={theme.palette.ensightful.neutral[400]}
                    />
                </SvgIcon>
            );
        } else {
            return error ? (
                <SvgIcon>
                    <rect
                        x={'0.5'}
                        y={'0.5'}
                        width={'23'}
                        height={'23'}
                        rx={'1.5'}
                        fill={theme.palette.ensightful.shades[0]}
                    />
                    <rect
                        x={'0.5'}
                        y={'0.5'}
                        width={'23'}
                        height={'23'}
                        rx={'1.5'}
                        fill={theme.palette.ensightful.error[50]}
                        stroke={theme.palette.ensightful.error[700]}
                    />
                </SvgIcon>
            ) : (
                <SvgIcon>
                    <rect
                        x={'0.5'}
                        y={'0.5'}
                        width={'23'}
                        height={'23'}
                        rx={'1.5'}
                        fill={theme.palette.ensightful.shades[0]}
                        stroke={theme.palette.ensightful.neutral[600]}
                    />
                </SvgIcon>
            );
        }
    }
};

const getTransparentIcon = (theme, checked, error, disabled) => {
    const borderColor = disabled
        ? theme.palette.ensightful.neutral[400]
        : error
          ? theme.palette.ensightful.error[700]
          : checked
            ? theme.palette.ensightful.primary[500]
            : theme.palette.ensightful.neutral[600];

    return (
        <SvgIcon>
            <rect x={'0.5'} y={'0.5'} width={'23'} height={'23'} rx={'1.5'} fill={'transparent'} stroke={borderColor} />
            {checked && (
                <path
                    d={
                        'M19.6867 6.34175C19.786 6.44981 19.8648 6.57817 19.9186 6.7195C19.9723 6.86082 20 7.01233 20 7.16534C20 7.31835 19.9723 7.46985 19.9186 7.61118C19.8648 7.7525 19.786 7.88087 19.6867 7.98893L10.0886 18.4583C9.98958 18.5666 9.8719 18.6525 9.74233 18.7112C9.61277 18.7698 9.47387 18.8 9.3336 18.8C9.19332 18.8 9.05442 18.7698 8.92486 18.7112C8.7953 18.6525 8.67761 18.5666 8.57855 18.4583L4.31275 13.8052C4.1125 13.5868 4 13.2905 4 12.9816C4 12.6727 4.1125 12.3765 4.31275 12.158C4.513 11.9396 4.7846 11.8169 5.0678 11.8169C5.35099 11.8169 5.62259 11.9396 5.82284 12.158L9.3336 15.9898L18.1766 6.34175C18.2757 6.23342 18.3933 6.14747 18.5229 6.08883C18.6525 6.03019 18.7914 6 18.9316 6C19.0719 6 19.2108 6.03019 19.3404 6.08883C19.4699 6.14747 19.5876 6.23342 19.6867 6.34175Z'
                    }
                    fill={disabled ? theme.palette.ensightful.neutral[500] : theme.palette.ensightful.primary[500]}
                />
            )}
        </SvgIcon>
    );
};

export default forwardRef(
    (
        {
            'aria-label': ariaLabel,
            appearance = 'default',
            sx,
            className,
            disabled,
            error,
            label,
            labelPlacement,
            noWrap = true,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();

        const getCheckboxIcon = (checked) => {
            if (appearance === 'transparent') return getTransparentIcon(theme, checked, error, disabled);
            if (appearance === 'transparent-unchecked' && !checked)
                return getTransparentIcon(theme, checked, error, disabled);
            return getIcon(theme, checked, error, disabled);
        };

        const controlId = uuid();

        const ControlComponent = (
            <CustomCheckbox
                ref={ref}
                id={controlId}
                checkedIcon={getCheckboxIcon(true)}
                disabled={disabled}
                error={error}
                icon={getCheckboxIcon(false)}
                inputProps={{ 'aria-label': ariaLabel, ...rest.inputProps }}
                {...rest}
                {...(label ? {} : { sx, className })}
            />
        );

        let labelColor = 'default';
        if (error) labelColor = 'error';
        if (disabled) labelColor = 'disabled';

        return label ? (
            <CustomFormControlLabel
                sx={sx}
                className={className}
                control={ControlComponent}
                disableTypography
                htmlFor={controlId}
                label={
                    typeof label === 'string' ? (
                        <Text color={labelColor} component={'span'} noWrap={noWrap} variant={'p2'}>
                            {label}
                        </Text>
                    ) : (
                        label
                    )
                }
                labelPlacement={labelPlacement}
            />
        ) : (
            ControlComponent
        );
    }
);
